import cx from 'classnames';
import { useMemo } from 'react';
import type { HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';
import {
  type Icon,
  assertIcon,
  iconFamilyClass
} from './fontAwesomeIconInternalModules/internalIconsModule';
export * from './fontAwesomeIconInternalModules/internalIconsModule';
import {
  modifierClassName,
  type AllowedModifier
} from './fontAwesomeIconInternalModules/modifiers';
export type { AllowedModifier } from './fontAwesomeIconInternalModules/modifiers';

export type Props = Omit<
  HTMLAttributes<HTMLElement>,
  | 'className'
  | 'style'
  | 'title'
  | 'aria-label'
  | 'aria-hidden'
  | 'role'
  | 'onClick'
> & {
  className?: string;
  icon: Icon;
  /**
   * A single or a list of modifiers without `fa-` prefix from one of these:
   *   - [Sizing Icons](https://docs.fontawesome.com/web/style/size)
   *   - [Fixed Width Icons](https://docs.fontawesome.com/web/style/fixed-width)
   *   - [Rotate Icons](https://docs.fontawesome.com/web/style/rotate)
   *   - [Animating Icons](https://docs.fontawesome.com/web/style/animate)
   *   - [Bordered & Pulled Icons](https://docs.fontawesome.com/web/style/pull)
   *   - [Accessibility-Minded Style Utilities](https://docs.fontawesome.com/web/dig-deeper/accessibility#accessibility-minded-style-utilities)
   */
  modifier?: AllowedModifier | readonly AllowedModifier[];
  style?: { [key: string]: string | number | undefined };
  title?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
  role?: string;
  onClick?: () => void;
  ref?: Ref<HTMLElement>;
};

function toModifierList(
  modifier: AllowedModifier | readonly AllowedModifier[] | undefined
): readonly AllowedModifier[] {
  if (!modifier) return [];
  if (typeof modifier === 'string') return [modifier];
  return modifier;
}

function FontAwesomeIcon(props: Props): ReactElement {
  const {
    icon,
    className,
    modifier,
    'aria-hidden': ariaHiddenProp,
    ref,
    ...passThroughProps
  } = props;

  const classNameForIcon = useMemo(() => {
    const [iconFamily, iconName] = assertIcon(icon);

    return cx(
      iconFamilyClass(iconFamily),
      `fa-${iconName}`,
      toModifierList(modifier).map(mod => modifierClassName(mod)),
      className
    );
  }, [icon, className, modifier]);

  const ariaHidden =
    typeof ariaHiddenProp === 'boolean' ? ariaHiddenProp : true;

  return (
    // eslint-disable-next-line rulesdir/no-plain-icons -- This is the safe call.
    <i
      {...passThroughProps}
      ref={ref}
      aria-hidden={ariaHidden}
      className={classNameForIcon}
    />
  );
}

FontAwesomeIcon.displayName = 'FontAwesomeIcon';

export default FontAwesomeIcon;

export type StackProps = {
  foregroundIcon: Icon;
  backgroundIcon: Icon;
};

export function FontAwesomeIconStack(props: StackProps): ReactElement {
  return (
    <FontAwesomeIconStackWrapper>
      <FontAwesomeIcon
        icon={props.backgroundIcon}
        className={modifierClassName('stack-2x')}
      />
      <FontAwesomeIcon
        icon={props.foregroundIcon}
        className={modifierClassName('stack-1x')}
      />
    </FontAwesomeIconStackWrapper>
  );
}

export function FontAwesomeIconStackWrapper({
  children
}: {
  children: ReactNode;
}): ReactElement {
  return <span className="fa-stack">{children}</span>;
}
